// src/pages/admin/AdminTowns.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { addDoc, collection, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { getCounties } from '../../lib/db';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const EMPTY = { name: '', county: '', countyId: '', population: '', status: 'soon', sponsorName: '', tagline: '', brandColors: [] };

const FIELDS = [
  ['Town name', 'name', 'e.g. Loughrea'],
  ['Population', 'population', 'e.g. 5,600'],
  ['Tagline', 'tagline', 'e.g. The heart of East Galway (optional, shown on the town page)'],
  ['Sponsor name', 'sponsorName', 'e.g. Loughrea Credit Union (optional)'],
];

const AdminTowns = () => {
  const navigate = useNavigate();
  const [towns, setTowns] = useState([]);
  const [counties, setCounties] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); loadCounties(); }, []);

  const loadCounties = async () => {
    const c = await getCounties();
    setCounties(c);
  };

  const load = async () => {
    const snap = await getDocs(collection(db, 'towns'));
    setTowns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.countyId) { toast.error('Town name and county are required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await updateDoc(doc(db, 'towns', editId), form);
        toast.success(`${form.name} updated`);
        setEditId(null);
      } else {
        await addDoc(collection(db, 'towns'), { ...form, createdAt: serverTimestamp() });
        toast.success(`${form.name} added`);
      }
      setForm(EMPTY);
      setAdding(false);
      load();
    } catch { toast.error('Failed to save town'); }
    setSaving(false);
  };

  const startEdit = (town) => {
    setForm({
      name: town.name || '',
      county: town.county || '',
      countyId: town.countyId || '',
      population: town.population || '',
      status: town.status || 'soon',
      sponsorName: town.sponsorName || '',
      tagline: town.tagline || '',
      brandColors: Array.isArray(town.brandColors) && town.brandColors.length > 0
        ? town.brandColors
        : (town.primaryColor ? [town.primaryColor] : []),
    });
    setEditId(town.id);
    setAdding(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await deleteDoc(doc(db, 'towns', id));
    toast.success('Town deleted');
    load();
  };

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate('/admin')} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Admin</span>
      </button>
      <div style={s.h1}>🏘️ Towns</div>

      <button onClick={() => { setAdding(!adding); setEditId(null); setForm(EMPTY); }}
        style={s.addBtn}>
        {adding ? '✕ Cancel' : '+ Add town'}
      </button>

      {adding && (
        <div style={s.formCard}>
          <div style={s.formTitle}>{editId ? 'Edit town' : 'New town'}</div>
          <div style={s.field}>
            <div style={s.label}>County</div>
            {counties.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 500 }}>
                No counties yet — <button onClick={() => navigate('/admin/counties')} style={{ background: 'none', border: 'none', color: 'var(--sage-dark)', fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>add one first</button>.
              </div>
            ) : (
              <select
                className="form-input"
                value={form.countyId}
                onChange={e => {
                  const chosen = counties.find(c => c.id === e.target.value);
                  setForm(f => ({ ...f, countyId: e.target.value, county: chosen?.name || '' }));
                }}
              >
                <option value="">Select a county...</option>
                {counties.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          </div>
          {FIELDS.map(([lbl, key, ph]) => (
            <div key={key} style={s.field}>
              <div style={s.label}>{lbl}</div>
              <input className="form-input" value={form[key]} onChange={e => set(key, e.target.value)} placeholder={ph} />
            </div>
          ))}
          <div style={s.field}>
            <div style={s.label}>Town colours (optional, up to 3)</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
              Shows as a bold banner at the top of the app for this town — great place for county colours. Leave blank to use the default sage green.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.brandColors.map((color, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="color"
                    value={color || '#7A9A7C'}
                    onChange={e => {
                      const next = [...form.brandColors];
                      next[i] = e.target.value;
                      set('brandColors', next);
                    }}
                    style={{ width: 44, height: 44, borderRadius: 10, border: '1.5px solid var(--border)', cursor: 'pointer', padding: 0, flexShrink: 0 }}
                  />
                  <input
                    className="form-input"
                    value={color}
                    onChange={e => {
                      const next = [...form.brandColors];
                      next[i] = e.target.value;
                      set('brandColors', next);
                    }}
                    placeholder="#7A9A7C"
                    style={{ flex: 1 }}
                  />
                  <button
                    onClick={() => set('brandColors', form.brandColors.filter((_, idx) => idx !== i))}
                    style={{ background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '10px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', flexShrink: 0 }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {form.brandColors.length < 3 && (
                <button
                  onClick={() => set('brandColors', [...form.brandColors, '#7A9A7C'])}
                  style={{ background: 'var(--white)', color: 'var(--sage-dark)', border: '1.5px dashed var(--sage-light)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}
                >
                  + Add {form.brandColors.length === 0 ? 'a colour' : 'another colour'} ({form.brandColors.length}/3)
                </button>
              )}
            </div>
            {form.brandColors.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>PREVIEW</div>
                <div style={{ display: 'flex', height: 14, borderRadius: 8, overflow: 'hidden' }}>
                  {form.brandColors.map((c, i) => (
                    <div key={i} style={{ flex: 1, background: c || '#7A9A7C' }} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div style={s.field}>
            <div style={s.label}>Status</div>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="soon">Coming soon</option>
              <option value="live">Live</option>
            </select>
          </div>
          <button onClick={handleSave} disabled={saving} style={s.saveBtn}>
            {saving ? 'Saving...' : editId ? 'Save changes' : 'Add town'}
          </button>
        </div>
      )}

      <div style={s.sectionLabel}>All towns ({towns.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {towns.map(town => (
          <div key={town.id} style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div style={s.cardTitle}>{town.name}</div>
              <span style={{ ...s.badge, background: town.status === 'live' ? 'var(--sage-pale)' : '#F0F0EE', color: town.status === 'live' ? 'var(--sage-deep)' : 'var(--muted)' }}>
                {town.status || 'soon'}
              </span>
            </div>
            <div style={s.cardSub}>{town.county} · Pop. {town.population || '—'}</div>
            {town.sponsorName && <div style={s.cardSub}>Sponsor: {town.sponsorName}</div>}
            <div style={s.btnRow}>
              <button onClick={() => startEdit(town)} style={s.editBtn}>Edit</button>
              <button onClick={() => handleDelete(town.id, town.name)} style={s.deleteBtn}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: 'var(--muted)', fontWeight: 600 },
  h1: { fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 },
  addBtn: { width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', marginBottom: 16 },
  formCard: { background: 'var(--white)', borderRadius: 18, padding: 18, marginBottom: 16, boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: 12 },
  formTitle: { fontSize: 16, fontWeight: 800, color: 'var(--ink)' },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)' },
  saveBtn: { background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
  sectionLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10 },
  card: { background: 'var(--white)', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--ink)' },
  cardSub: { fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 4 },
  badge: { fontSize: 10, padding: '2px 8px', borderRadius: 100, fontWeight: 700, flexShrink: 0 },
  btnRow: { display: 'flex', gap: 8, marginTop: 10 },
  editBtn: { flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
  deleteBtn: { background: 'var(--error-pale)', color: 'var(--error)', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
};

export default AdminTowns;
